// OEE (Overall Equipment Effectiveness) for one machine on one day, from live shop floor data.
//
//   Availability = run time / planned time          (planned = shift hours − breaks, run = planned − downtime)
//   Performance  = units made / units the machine could make in the run time (capacity per hour)
//   Quality      = good units / units made
//   OEE          = Availability × Performance × Quality

export interface OEEShift {
  id: string;
  startTime: string; // HH:MM[:SS]
  endTime: string;
  breakMinutes?: number;
  workingDays?: string[]; // e.g. ['Mon', 'Tue']
  isActive?: boolean;
}

export interface OEEMachine {
  id: string;
  capacityPerHour?: number;
}

export interface OEEProductionEntry {
  machineId: string | null;
  shiftId?: string | null;
  date: string; // YYYY-MM-DD (local)
  producedQty: number; // good units
  rejectedQty: number;
}

export interface OEEDowntimeEntry {
  machineId: string;
  shiftId?: string | null;
  date: string;
  startTime?: string | null;
  durationMinutes?: number | null;
  isResolved?: boolean;
}

export interface OEEData {
  shifts: OEEShift[];
  productionEntries: OEEProductionEntry[];
  downtimeEntries: OEEDowntimeEntry[];
}

export interface OEEResult {
  availability: number;
  performance: number;
  quality: number;
  oee: number;
  plannedMinutes: number;
  downtimeMinutes: number;
  runTime: number;
  producedQty: number;
  rejectedQty: number;
  /** False when the machine has no capacity per hour, so performance cannot be measured */
  hasCapacity: boolean;
  /** False when there is nothing to measure: no shift planned, or no output and no downtime */
  hasData: boolean;
  asPercent: {
    availability: number;
    performance: number;
    quality: number;
    oee: number;
  };
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function minutesOfDay(time?: string | null): number | null {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(time || ''));
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/** Working minutes of a shift (handles shifts that run past midnight), net of breaks */
export function shiftWorkingMinutes(shift: OEEShift): number {
  const start = minutesOfDay(shift.startTime);
  const end = minutesOfDay(shift.endTime);
  if (start === null || end === null) return 0;
  let minutes = end - start;
  if (minutes <= 0) minutes += 24 * 60;
  return Math.max(0, minutes - (Number(shift.breakMinutes) || 0));
}

function worksOn(shift: OEEShift, date: string): boolean {
  if (shift.isActive === false) return false;
  if (!Array.isArray(shift.workingDays) || shift.workingDays.length === 0) return true;
  const day = WEEKDAYS[new Date(`${date}T00:00:00`).getDay()];
  return shift.workingDays.some((d) => String(d).slice(0, 3).toLowerCase() === day.toLowerCase());
}

export function calculateOEE(machine: OEEMachine, date: string, shiftId: string, data: OEEData): OEEResult {
  const forShift = (rowShiftId?: string | null) => shiftId === 'all' || rowShiftId === shiftId;

  // Planned production time
  const shifts = data.shifts.filter((s) => (shiftId === 'all' || s.id === shiftId) && worksOn(s, date));
  const plannedMinutes = shifts.reduce((sum, s) => sum + shiftWorkingMinutes(s), 0);

  // Downtime — a stoppage still open today counts up to the current time
  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const isToday =
    date === `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const downtimeMinutes = data.downtimeEntries
    .filter((d) => d.machineId === machine.id && d.date === date && forShift(d.shiftId))
    .reduce((sum, d) => {
      if (d.isResolved || Number(d.durationMinutes) > 0) return sum + (Number(d.durationMinutes) || 0);
      const start = minutesOfDay(d.startTime);
      return sum + (isToday && start !== null ? Math.max(0, nowMinutes - start) : 0);
    }, 0);

  const runTime = Math.max(0, plannedMinutes - Math.min(downtimeMinutes, plannedMinutes));
  const availability = plannedMinutes > 0 ? runTime / plannedMinutes : 0;

  // Output
  const entries = data.productionEntries.filter(
    (e) => e.machineId === machine.id && e.date === date && forShift(e.shiftId),
  );
  const producedQty = entries.reduce((sum, e) => sum + (Number(e.producedQty) || 0), 0);
  const rejectedQty = entries.reduce((sum, e) => sum + (Number(e.rejectedQty) || 0), 0);
  const totalQty = producedQty + rejectedQty;

  const capacityPerHour = Number(machine.capacityPerHour) || 0;
  const hasCapacity = capacityPerHour > 0;
  const idealOutput = hasCapacity ? (runTime / 60) * capacityPerHour : 0;
  const performance = idealOutput > 0 ? Math.min(1, totalQty / idealOutput) : 0;

  const quality = totalQty > 0 ? producedQty / totalQty : 0;
  const oee = availability * performance * quality;
  const hasData = plannedMinutes > 0 && (totalQty > 0 || downtimeMinutes > 0);

  const pct = (v: number) => Math.round(Math.max(0, Math.min(1, v)) * 100);
  return {
    availability,
    performance,
    quality,
    oee,
    plannedMinutes,
    downtimeMinutes,
    runTime,
    producedQty,
    rejectedQty,
    hasCapacity,
    hasData,
    asPercent: {
      availability: pct(availability),
      performance: pct(performance),
      quality: pct(quality),
      oee: pct(oee),
    },
  };
}

/**
 * Plant OEE for a day from all of its output — entries without a machine included — so the plant
 * figure always reflects what was produced.
 *   Availability: planned shift time against the average downtime of the machines that ran
 *   Performance:  units on machines with a capacity / what those machines could make (not measured
 *                 when no machine with a capacity ran: counted as 100%, `hasCapacity` false)
 *   Quality:      good units / units made, over every entry
 */
export function calculatePlantOEE(machines: OEEMachine[], date: string, shiftId: string, data: OEEData): OEEResult {
  const forShift = (rowShiftId?: string | null) => shiftId === 'all' || rowShiftId === shiftId;

  const shifts = data.shifts.filter((s) => (shiftId === 'all' || s.id === shiftId) && worksOn(s, date));
  const plannedMinutes = shifts.reduce((sum, s) => sum + shiftWorkingMinutes(s), 0);

  const entries = data.productionEntries.filter((e) => e.date === date && forShift(e.shiftId));
  const producedQty = entries.reduce((sum, e) => sum + (Number(e.producedQty) || 0), 0);
  const rejectedQty = entries.reduce((sum, e) => sum + (Number(e.rejectedQty) || 0), 0);
  const totalQty = producedQty + rejectedQty;

  // Downtime per machine, averaged over the machines that ran or stopped
  const machineIds = new Set<string>();
  entries.forEach((e) => e.machineId && machineIds.add(e.machineId));
  const downtimes = data.downtimeEntries.filter((d) => d.date === date && forShift(d.shiftId));
  downtimes.forEach((d) => d.machineId && machineIds.add(d.machineId));
  const totalDowntime = [...machineIds].reduce(
    (sum, id) => sum + calculateOEE({ id }, date, shiftId, { ...data, productionEntries: [] }).downtimeMinutes,
    0,
  );
  const downtimeMinutes = machineIds.size > 0 ? totalDowntime / machineIds.size : 0;
  const runTime = Math.max(0, plannedMinutes - Math.min(downtimeMinutes, plannedMinutes));
  const availability = plannedMinutes > 0 ? runTime / plannedMinutes : 0;

  // Performance over the machines whose capacity is known
  const byId = new Map(machines.map((m) => [m.id, m]));
  let capacityUnits = 0;
  let idealOutput = 0;
  machineIds.forEach((id) => {
    const capacity = Number(byId.get(id)?.capacityPerHour) || 0;
    if (capacity <= 0) return;
    const ran = calculateOEE({ id, capacityPerHour: capacity }, date, shiftId, data);
    capacityUnits += ran.producedQty + ran.rejectedQty;
    idealOutput += (ran.runTime / 60) * capacity;
  });
  const hasCapacity = idealOutput > 0;
  const performance = hasCapacity ? Math.min(1, capacityUnits / idealOutput) : totalQty > 0 ? 1 : 0;

  const quality = totalQty > 0 ? producedQty / totalQty : 0;
  const oee = availability * performance * quality;
  const hasData = plannedMinutes > 0 && (totalQty > 0 || downtimeMinutes > 0);

  const pct = (v: number) => Math.round(Math.max(0, Math.min(1, v)) * 100);
  return {
    availability,
    performance,
    quality,
    oee,
    plannedMinutes,
    downtimeMinutes,
    runTime,
    producedQty,
    rejectedQty,
    hasCapacity,
    hasData,
    asPercent: {
      availability: pct(availability),
      performance: pct(performance),
      quality: pct(quality),
      oee: pct(oee),
    },
  };
}
